// src/Phaser/engine/entities/TextObject.ts
import Phaser from "phaser";
import type { TextObjectConfig } from "../../types/config";

/**
 * Représente un objet texte Phaser (UI/label, sans physique).
 */
export class TextObject {
  private adminMode: boolean;
  public id: string;

  // Coordonnées logiques
  public x: number;
  public y: number;

  // Données texte
  public textString: string;
  private fontSize: number;
  private color: string;
  private backgroundColor: string;
  private fontFamily: string;

  // Phaser
  private scene?: Phaser.Scene;
  public textObj?: Phaser.GameObjects.Text;

  // Listener pour cleanup
  private onSceneShutdown?: () => void;
  private onSceneDestroy?: () => void;

  constructor(adminMode: boolean, config: TextObjectConfig) {
    this.adminMode = adminMode;
    this.id = config.id;
    this.x = config.x;
    this.y = config.y;

    this.textString = config.textString || "";
    this.fontSize = config.fontSize ?? 16;
    this.color = config.color ?? "#ffffff";
    this.backgroundColor = config.backgroundColor ?? "";
    this.fontFamily = config.fontFamily ?? "Arial";
  }

  /** Création du Text dans la scène. */
  public createPhaserText(scene: Phaser.Scene) {
    this.scene = scene;

    this.textObj = scene.add.text(this.x, this.y, this.textString, {
      fontSize: `${this.fontSize}px`,
      fontFamily: this.fontFamily,
      color: this.color,
      backgroundColor: this.backgroundColor,
      // align: "center",
      // fontStyle: "bold",
      // wordWrap: { width: 300, useAdvancedWrap: true },
    });

    // Par défaut: coin haut-gauche
    this.textObj.setOrigin(0, 0);

    // Très au-dessus pour rester visible sur tout
    this.textObj.setDepth(99999);

    // Cleanup quand la scène s'arrête / détruit
    this.onSceneShutdown = () => this.destroy();
    this.onSceneDestroy = () => this.destroy();
    scene.events.once(Phaser.Scenes.Events.SHUTDOWN, this.onSceneShutdown);
    scene.events.once(Phaser.Scenes.Events.DESTROY, this.onSceneDestroy);

    if (this.adminMode) {
      // Tu peux tracer un cadre si tu veux
      // const g = scene.add.graphics().lineStyle(1, 0xff00ff, 0.8);
      // const b = this.textObj.getBounds();
      // g.strokeRect(b.x, b.y, b.width, b.height);
      // g.setDepth(this.textObj.depth + 1);
    }
  }

  /** Synchronise la position logique -> Phaser (si tu modifies x/y ailleurs). */
  public updatePhaserText() {
    if (!this.textObj) return;
    this.textObj.setPosition(this.x, this.y);
  }

  // -------- Mutateurs “public API” (gardent la logique & dispatch) --------

  public setText(newText: string) {
    const normalized = newText == null ? "" : String(newText);
    const oldText = this.textString;
    this.textString = normalized;
    this.textObj?.setText(normalized);

    const em = (window as any)?.eventManager;
    if (em) {
      em.dispatchTextStartsWithEvent?.(this.id, normalized);
      em.dispatchTextEndsWithEvent?.(this.id, normalized);
      em.dispatchTextChangeEvent?.(this.id, normalized, oldText);
    }
  }

  public getText(): string {
    return this.textString;
  }

  public addText(extra: string) {
    this.setText(this.textString + extra);
  }

  public replaceText(oldSubstring: string, newSubstring: string) {
    this.setText(this.textString.replace(oldSubstring, newSubstring));
  }

  public setPosition(newX: number, newY: number) {
    this.x = newX;
    this.y = newY;
    this.textObj?.setPosition(newX, newY);
  }

  public setFontSize(size: number) {
    this.fontSize = size;
    this.textObj?.setFontSize(size);
  }

  public setColor(newColor: string) {
    this.color = newColor;
    this.textObj?.setColor(newColor);
  }

  public setBackgroundColor(bg: string) {
    this.backgroundColor = bg;
    this.textObj?.setBackgroundColor(bg);
  }

  public setFontFamily(fontName: string) {
    this.fontFamily = fontName;
    this.textObj?.setStyle({ fontFamily: fontName });
  }

  // -------- Utilitaires (optionnels mais pratiques) --------

  public setOrigin(x: number, y: number) {
    this.textObj?.setOrigin(x, y);
  }

  public setDepth(depth: number) {
    this.textObj?.setDepth(depth);
  }

  public setScrollFactor(fx: number, fy?: number) {
    this.textObj?.setScrollFactor(fx, fy ?? fx);
  }

  public setVisible(v: boolean) {
    this.textObj?.setVisible(v);
  }

  public setAlpha(a: number) {
    this.textObj?.setAlpha(a);
  }

  public setStyle(style: Phaser.Types.GameObjects.Text.TextStyle) {
    this.textObj?.setStyle(style);
  }

  /** Destruction propre + retrait des listeners. */
  public destroy() {
    if (this.scene) {
      if (this.onSceneShutdown) {
        this.scene.events.off(Phaser.Scenes.Events.SHUTDOWN, this.onSceneShutdown);
        this.onSceneShutdown = undefined;
      }
      if (this.onSceneDestroy) {
        this.scene.events.off(Phaser.Scenes.Events.DESTROY, this.onSceneDestroy);
        this.onSceneDestroy = undefined;
      }
    }
    this.textObj?.destroy();
    this.textObj = undefined;
    this.scene = undefined;
  }
}
